<template>
  <div class="metrics-grade-distribution">
    <div class="metrics-grade-distribution__card">
      <span class="metrics-grade-distribution__label paragraph-s">
        {{ $t("metrics.classAverage") }}
      </span>
      <span class="metrics-grade-distribution__value">
        {{ overallAvg != null ? `${overallAvg}%` : "–" }}
      </span>
    </div>
    <div
      v-for="tier in sortedTiers"
      :key="tier.key"
      class="metrics-grade-distribution__card"
      :class="{ 'metrics-grade-distribution__card--clickable': clickable }"
      v-bind="clickable ? { role: 'button', tabindex: 0 } : {}"
      @click="onTierClick(tier.key)"
      @keydown.enter="onTierClick(tier.key)"
      @keydown.space.prevent="onTierClick(tier.key)"
    >
      <div class="metrics-grade-distribution__label paragraph-s">
        <span
          class="metrics-grade-distribution__dot"
          :style="{ background: tier.color }"
        />
        {{ tier.subtitle }}
      </div>
      <span class="metrics-grade-distribution__value">{{ tier.count }}</span>
    </div>
  </div>
</template>

<script>
import { METRICS_CLASS_TIER_COLORS as TIER_COLORS } from "@/constants";

const TIER_META = {
  high: { label: "Strong", min: 85, max: 100 },
  medium: { label: "Growing", min: 66, max: 84 },
  low: { label: "Need Support", min: 0, max: 65 },
};

/** Pre-composed i18n keys (no {min} placeholders) — matches sidebar / chart tier labels. */
const TIER_LABEL_I18N_KEY = {
  high: "metrics.tierSidebarHigh",
  medium: "metrics.tierSidebarMedium",
  low: "metrics.tierSidebarLow",
};

export default {
  name: "MetricsGradeDistribution",

  props: {
    overallAvg: {
      type: Number,
      default: null,
    },
    grades: {
      type: Object,
      default: null,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    sortedTiers() {
      if (!this.grades) return [];
      return Object.entries(this.grades)
        .map(([key, count]) => {
          const meta = TIER_META[key] || { min: 0, max: 100 };
          return {
            key,
            count,
            ...meta,
            subtitle: this.tierLabel(key, meta),
            color: TIER_COLORS[key] || null,
          };
        })
        .sort((a, b) => b.min - a.min);
    },
  },

  methods: {
    onTierClick(key) {
      if (this.clickable) this.$emit("tier-click", key);
    },

    /**
     * Standard tiers (high / medium / low) use static translations shared with
     * the students sidebar, avoiding placeholder parsing edge cases. Unknown
     * group keys still use parameterized strings.
     */
    tierLabel(tierKey, grade) {
      if (TIER_LABEL_I18N_KEY[tierKey]) {
        return this.$t(TIER_LABEL_I18N_KEY[tierKey]);
      }
      if (grade.min === 0) {
        return this.$t("metrics.tierStudentsLt", { max: grade.max });
      }
      if (grade.max === 100) {
        return this.$t("metrics.tierStudentsGte", { min: grade.min });
      }
      return this.$t("metrics.tierStudentsRange", {
        min: grade.min - 1,
        max: grade.max,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "metrics_card";

.metrics-grade-distribution {
  display: flex;
  gap: 16px;
  padding: 0 24px;
  @media (max-width: 1024px) {
    gap: 12px;
    flex-wrap: wrap;
  }

  @media (max-width: 600px) {
    flex-direction: column;
  }

  &__card {
    @include metrics-card;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 0;

    &--clickable {
      cursor: pointer;
      transition: border-color 0.15s;

      &:hover {
        border-color: var(--color-stroke-brand-default, #7c3aed);
      }

      &:focus-visible {
        outline: 2px solid var(--color-stroke-primary);
      }
    }

    @media (max-width: 1024px) {
      padding: 16px;
      flex-basis: calc(50% - 6px);
    }

    @media (max-width: 600px) {
      flex-basis: auto;
    }
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--color-text-neutral-fade);
    font-weight: var(--typography-font-weight-fw-600, 600);
  }

  &__dot {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    border-radius: 4px;
  }

  &__value {
    color: var(--color-text-neutral-on-white);
    font-family: var(--typography-font-family-text-primary, Inter);
    font-size: 32px;
    font-weight: var(--typography-font-weight-fw-800, 800);
    line-height: 120%;

    @media (max-width: 1024px) {
      font-size: 24px;
    }
  }

  &__subtitle {
    color: var(--color-text-primary-default, #7c3aed);
    font-size: var(--typography-font-size-fs-200);
    font-weight: var(--typography-font-weight-fw-700, 700);
    line-height: 140%;
  }
}
</style>
